import { useCallback, useContext, useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, Dialog, Grid, IconButton, Snackbar, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import dayjs from 'helpers/dayjs';
import { numberFormat } from 'helpers/i18n';
// Our Components
import { AuthContext } from 'context/auth-context';
import { CardContext } from 'context/cards/cardContext';
import {
    CARD_FORM_ERROR,
    CLOSE_EDIT_CARD_FORM_MODAL,
    OPEN_CONFIRM_EXIT_DIALOG,
    UPDATE_CARD_LIST,
    UPDATE_CARDS_METADATA
} from 'context/cards/types';
import EditCardForm from './EditCardForm';
// Services
import { Cards, Overview } from 'lib/api';
// Utils and Helpers
import { MUI_CLOSE_MODAL_REASONS } from 'helpers/constants';
import { CARD_TYPE } from 'helpers/constants';
// Assets
import physicalCardBackside from 'assets/cardsSection/cardBackgroud.svg';
import virtualCardBackside from 'assets/createCards/virtualCardBackside.png';
import './EditCard.css';

const CARD_VALUES_TO_UPDATE = {
    autoLock: '',
    label: '',
    memo: ''
};
const EditCard = () => {
    const { card, dispatch, errorMessage, isEditCardFormModalOpen, isFormSubmitting, previousCard } = useContext(CardContext);
    const { businessInfo } = useContext(AuthContext);
    const [didFormValuesChange, setDidFormValuesChange] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [newCardLimit, setNewCardLimit] = useState('0');
    const [snackbar, setSnackbar] = useState({
        open: false,
        anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
        }
    });

    useEffect(() => {
        setNewCardLimit(previousCard.limitAmount);
    }, [previousCard.limitAmount]);

    const fetchOverviewInfo = useCallback(async () => {
        try {
            const statistics = await Overview.getOverviewInfo();
            dispatch({
                type: UPDATE_CARDS_METADATA,
                payload: statistics
            });
        } catch (error) {
            console.error(error);
        }
    }, [dispatch]);

    const handleCardFormModalClose = (event, reason) => {
        if (isFormSubmitting) return;
        if (card?.id && didFormValuesChange && MUI_CLOSE_MODAL_REASONS.includes(reason)) {
            return dispatch({ type: OPEN_CONFIRM_EXIT_DIALOG });
        }
        dispatch({ type: CLOSE_EDIT_CARD_FORM_MODAL });
    };

    const handleSubmit = async (values, actions) => {
        setIsSubmitting(true);
        try {
            if (previousCard.limitAmount !== values.limitAmount) {
                const bodyFundingAmount = {
                    fundingAmount: values.limitAmount
                };
                const updatedCardLimit = await Cards.addFunds(card?.id, bodyFundingAmount);
                setNewCardLimit(values.limitAmount);
                dispatch({
                    type: UPDATE_CARD_LIST,
                    payload: updatedCardLimit
                });
            }

            const body = mapValuesToUpdateCardModel(values);
            const updatedCard = await Cards.updateCard(body, card?.id);
            dispatch({
                type: UPDATE_CARD_LIST,
                payload: updatedCard
            });
            fetchOverviewInfo();
            dispatch({ type: CLOSE_EDIT_CARD_FORM_MODAL });
            setSnackbar({ ...snackbar, open: true });
        } catch (error) {
            dispatch({
                type: CARD_FORM_ERROR,
                payload: error?.response?.data?.message
            });
        }
        setIsSubmitting(false);
    };
    const mapValuesToUpdateCardModel = currentCardValues => {
        const body = {};
        for (const key of Object.keys(currentCardValues)) {
            if (
                key in CARD_VALUES_TO_UPDATE &&
                currentCardValues[key] &&
                key === 'autoLock' &&
                dayjs(previousCard[key]).isSame(currentCardValues[key])
            ) {
                continue;
            }
            if (key === 'autoLock' && previousCard[key] !== currentCardValues[key]) {
                body[key] = currentCardValues[key]
                    ? dayjs(currentCardValues[key]).tz('America/Los_Angeles').startOf('day').toISOString()
                    : null;
            }
            if (key === 'memo' && !currentCardValues[key]) {
                body[key] = '';
            }
            if (key in CARD_VALUES_TO_UPDATE && currentCardValues[key] && previousCard[key] !== currentCardValues[key]) {
                body[key] = currentCardValues[key];
            }
        }
        return body;
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbar({ ...snackbar, open: false });
    };

    return (
        <>
            <Dialog open={isEditCardFormModalOpen} onClose={() => {}} fullWidth maxWidth="md">
                <Box className="edit-card-form-modal w-100">
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="30px">
                        <Box className="modal-title">Edit Card</Box>

                        <Box alignSelf="start">
                            <IconButton onClick={handleCardFormModalClose}>
                                <Close sx={{ fontSize: '20px', cursor: 'pointer' }} />
                            </IconButton>
                        </Box>
                    </Box>
                    <Grid container columnSpacing={{ md: 3 }}>
                        <Grid item md={6}>
                            <Box sx={{ marginBottom: '12px' }}>
                                <EditCardForm onSubmit={handleSubmit} setDidFormValuesChange={setDidFormValuesChange} />
                            </Box>
                        </Grid>
                        <Grid item md={6}>
                            <Box
                                className="card-banner-container"
                                sx={{
                                    background: 'linear-gradient(to bottom, rgba(27, 171, 226, 0.2) 50%, #f9f9f8 50%)'
                                }}>
                                <Box className="card-image-container" position="relative">
                                    <img
                                        className="card-image"
                                        src={card.type === CARD_TYPE.VIRTUAL ? virtualCardBackside : physicalCardBackside}
                                        alt="virtual card"
                                    />
                                    <Typography component="div" className="card-secret card-number" sx={{ fontSize: '12px' }}>
                                        **** **** **** {card?.solidData?.last4}
                                    </Typography>
                                    <Typography component="div" className="card-secret card-expiration" sx={{ fontSize: '12px' }}>
                                        **/**
                                    </Typography>
                                    <Typography component="div" className="card-secret card-cvv" sx={{ fontSize: '12px' }}>
                                        ***
                                    </Typography>

                                    <Typography
                                        component="div"
                                        className="card-backside-cardholder-name"
                                        sx={{
                                            color: `${
                                                card.type === CARD_TYPE.VIRTUAL ? 'var(--white-color)' : 'var(--main-color)'
                                            }`
                                        }}>
                                        {card?.name}
                                    </Typography>
                                    <Typography
                                        component="div"
                                        className="card-backside-cardholder-company"
                                        sx={{
                                            color: `${
                                                card?.type === CARD_TYPE.VIRTUAL ? 'var(--white-color)' : 'var(--main-color)'
                                            }`
                                        }}>
                                        {businessInfo.legal_name}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                    {errorMessage && (
                        <Alert severity="error" sx={{ marginTop: '20px' }}>
                            {errorMessage}
                        </Alert>
                    )}
                    <Box display="flex" justifyContent="end" sx={{ marginTop: '20px' }}>
                        <Button
                            variant="contained"
                            type="submit"
                            form="edit-card-form"
                            disabled={isSubmitting || !didFormValuesChange}>
                            {isSubmitting ? <CircularProgress size="20px" /> : 'Save'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
            <Snackbar
                anchorOrigin={snackbar.anchorOrigin}
                open={snackbar.open}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                autoHideDuration={3000}
                action={
                    <>
                        <Button color="secondary" size="small" onClick={handleSnackbarClose}></Button>
                        <IconButton size="small" aria-label="close" color="inherit" onClick={handleSnackbarClose}>
                            <Close fontSize="small" />
                        </IconButton>
                    </>
                }>
                {newCardLimit !== previousCard?.limitAmount ? (
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity="success"
                        sx={{
                            backgroundColor: '#FFF',
                            fontWeight: 'normal',
                            fontSize: '14px',
                            lineHeight: '22px',
                            color: '#182D55'
                        }}>
                        <AlertTitle sx={{ fontWeight: 'bold' }}>
                            {`Card (${previousCard?.solidData?.last4}) edited successfully`}
                        </AlertTitle>
                        {`New Card Limit ${numberFormat(newCardLimit || 0, 2, 2)}`}
                    </Alert>
                ) : (
                    <Alert
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        severity="success"
                        sx={{
                            backgroundColor: '#FFF',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            lineHeight: '22px',
                            color: '#182D55'
                        }}>
                        {`Card ${previousCard?.solidData?.last4} updated successfully`}
                    </Alert>
                )}
            </Snackbar>
        </>
    );
};

export default EditCard;
